import classNames from 'classnames';
import A from 'components/Link/Link';
import OptionedMenu, { OptionedMenuItem } from 'components/OptionedMenu/OptionedMenu';
import ProfileImage from 'components/ProfileImage/ProfileImage';
import Rating, { RatingFeedbacks } from 'components/Rating/Rating';
import OrderProcessingTime from 'components/Shop/OrderProcessingTime';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { setGlobalLoader } from 'store/actions/global-loader.actions';
import { ProductType } from 'types/product.types';
import { UserType } from 'types/user.types';

type Props = {
  name: string;
  slug: string;
  showSlug?: boolean;
  lastActive?: string;
  imageSrc: string;
  href?: string;
  as?: string;
  disabled?: boolean;
  className?: string;
  imageSize?: number;
  globalLoaderEnabled?: boolean; // Show global loader on click, if enabled,
  actionsList?: OptionedMenuItem[];
  showActions?: boolean;
  product?: ProductType;
  user?: UserType;
  refreshParentDataFn?: () => void;
  orderCount?: string;
  likeCount?: number;
  formattedOrderProcessingTime?: string;
  feedbacks?: RatingFeedbacks;
  compactMeta?: boolean;
  rightContent?: React.ReactNode;
  onClick?: () => void;
};

const BlockShop = (props: Props) => {
  const { t } = useTranslation();
  const {
    name,
    slug,
    imageSrc,
    href,
    as,
    disabled,
    className,
    imageSize,
    globalLoaderEnabled,
    showSlug = true,
    lastActive,
    actionsList = [],
    showActions = false,
    product,
    user,
    refreshParentDataFn,
    orderCount,
    likeCount,
    formattedOrderProcessingTime,
    feedbacks,
    compactMeta = false,
    rightContent,
    onClick,
  } = props;

  const handleClick = (e) => {
    onClick?.();
    globalLoaderEnabled && setGlobalLoader(true, e);
  };

  const hasRating = !!feedbacks;

  return (
    <div className="block-shop__wrapper">
      <div className={classNames('block-shop', className, { disabled, 'block-shop--compact': compactMeta })}>
        {/* image */}
        <A className="no-style no-underline always shop-image-link" href={href} as={as} onClick={handleClick}>
          <ProfileImage src={imageSrc} size={imageSize || 45} className="shop-image" />
        </A>

        {/* details */}
        <div className="shop-details">
          {/* name */}
          <A className="no-style no-underline always" href={href} as={as} onClick={handleClick}>
            <h6 className="shop-name">{name}</h6>

            {/* slug */}
            {showSlug && !compactMeta && <div className="shop-slug">@{slug}</div>}
          </A>

          {compactMeta && (
            <div className="shop-meta">
              {showSlug && (
                <A className="no-style no-underline always shop-slug" href={href} as={as} onClick={handleClick}>
                  @{slug}
                </A>
              )}
              {hasRating && (
                <div className="shop-meta__row">
                  <span className="shop-meta__item">
                    <span className="shop-meta__dot">·</span>
                    {feedbacks?.total ? (
                      <Rating feedbacks={feedbacks} starSize={14} />
                    ) : (
                      <span className="shop-meta__no-reviews">{t('components.feedback.no-reviews')}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* rating */}
          {!compactMeta && feedbacks && <Rating feedbacks={feedbacks} />}

          {/* activity */}
          {lastActive && (
            <div className="shop-activity">
              <img src="/icons/icn-activity.svg" className="activity-icon" alt="activity" />
              <span className="shop-activity-text">{lastActive}</span>
            </div>
          )}

          <div className="shop-stats">
            {orderCount && (
              <div className="shop-stats-likes">
                <b>{orderCount}</b> {t('components.page-shop.items-sold')}
              </div>
            )}

            {Number.isInteger(+likeCount) && (
              <div className="shop-stats-sold">
                <b>{+likeCount}</b> {t('components.page-shop.followers', { count: +likeCount })}
              </div>
            )}
          </div>

          <OrderProcessingTime text={formattedOrderProcessingTime} classes={'shop-stats-shipsin'} iconHidden={true} />
        </div>
      </div>
      {rightContent && <div className="shop-actions">{rightContent}</div>}
      {actionsList.length > 0 && user && product && showActions && (
        <div className="shop-actions">
          {!product?.isOwner && (
            <OptionedMenu user={user} targetUser={product.user} targetShop={product.shop} refreshParentDataFn={refreshParentDataFn} />
          )}
        </div>
      )}
    </div>
  );
};

export default BlockShop;
